import posthog from 'posthog-js';

// ── PostHog analytics ─────────────────────────────────────────────────────────
// Single entry point for product analytics. Nothing else in the app imports
// posthog-js directly, so:
//   • every call is a no-op when no project token is configured (open-source
//     checkouts, CI, and local dev without a token all keep working);
//   • analytics can never throw into a render path — every call is guarded.
//
// CRA only inlines env vars prefixed REACT_APP_, so that's the prefix used here
// (a VITE_-prefixed var is silently dropped at build time by webpack's
// DefinePlugin and would arrive as undefined).
const TOKEN = process.env.REACT_APP_POSTHOG_PROJECT_TOKEN || '';
// Events go through our own reverse proxy so ad blockers and corporate DNS
// (which routinely block *.posthog.com) don't silently drop analytics.
const HOST  = process.env.REACT_APP_POSTHOG_HOST || 'https://k.learnxops.com';
// Where the PostHog app itself lives. Without this, the toolbar and the
// "view in PostHog" links generated by the SDK would point at the proxy domain,
// which serves the ingestion API but not the UI.
const UI_HOST = process.env.REACT_APP_POSTHOG_UI_HOST || 'https://us.posthog.com';

// Session replay and error tracking are on by default and can each be turned
// off with an env var. Replay runs with aggressive masking rather than not at
// all — see SESSION_RECORDING_OPTIONS below — because this app renders resumes,
// cover letters, member API keys and live terminals that stream real
// credentials.
const RECORDING_ENABLED = process.env.REACT_APP_POSTHOG_SESSION_RECORDING !== 'false';
const ERRORS_ENABLED    = process.env.REACT_APP_POSTHOG_ERROR_TRACKING !== 'false';
// Console logs are attached to replays so a support person can read the error
// alongside the clicks that produced it. Off by default in dev noise terms only.
const CONSOLE_LOGS_ENABLED = process.env.REACT_APP_POSTHOG_CONSOLE_LOGS !== 'false';

/*
 * How many sessions get recorded, 0 to 1.
 *
 * The replay recorder is a 64KB script fetched on top of posthog-js, and it
 * was loading for every visitor on every page. Replay is worth keeping — the
 * masking rules below exist because support genuinely uses it — but it is
 * worth keeping on a sample. Nobody watches a hundred percent of sessions.
 *
 * The decision is taken once per browser session and cached, so a recording
 * never starts halfway through someone's visit and no session is half
 * captured.
 */
const RECORDING_SAMPLE = (() => {
  const raw = parseFloat(process.env.REACT_APP_POSTHOG_RECORDING_SAMPLE);
  return Number.isFinite(raw) && raw >= 0 && raw <= 1 ? raw : 0.2;
})();

function sampledIntoRecording() {
  if (!RECORDING_ENABLED) return false;
  if (RECORDING_SAMPLE >= 1) return true;
  if (RECORDING_SAMPLE <= 0) return false;
  try {
    const cached = sessionStorage.getItem('lxops_rec');
    if (cached !== null) return cached === '1';
    const pick = Math.random() < RECORDING_SAMPLE;
    sessionStorage.setItem('lxops_rec', pick ? '1' : '0');
    return pick;
  } catch {
    // Private mode and blocked storage: fall back to a per-load draw rather
    // than recording everyone.
    return Math.random() < RECORDING_SAMPLE;
  }
}

// Regions that must never be recorded as readable text.
//   • .xterm            — live lab terminals; command output routinely contains
//                         kubeconfigs, cloud credentials and tokens.
//   • .ph-mask          — PostHog's own opt-in masking class.
//   • [data-ph-mask]    — attribute form, for components that already own their
//                         className.
const MASK_TEXT_SELECTOR = '.xterm, .ph-mask, [data-ph-mask]';
// Regions omitted from the recording entirely (not even layout is kept).
const BLOCK_SELECTOR = '.ph-no-capture, [data-ph-no-capture]';

const SESSION_RECORDING_OPTIONS = {
  // Every input is masked — this covers passwords, the BYOK API-key fields,
  // resume/cover-letter form fields and anything a member types into a lab.
  maskAllInputs: true,
  maskTextSelector: MASK_TEXT_SELECTOR,
  blockSelector: BLOCK_SELECTOR,
  // PostHog's conventional class names, set explicitly so they can't drift.
  maskTextClass: 'ph-mask',
  blockClass: 'ph-no-capture',
  // Never record network headers or bodies: every API call on this app carries
  // a JWT in the Authorization header, and responses contain member PII.
  recordHeaders: false,
  recordBody: false,
};

// Unhandled errors and promise rejections become PostHog exceptions.
// capture_console_errors stays off: xterm, Electron and the embedded editors
// log routinely to console.error, which would bury real crashes.
const EXCEPTION_OPTIONS = {
  capture_unhandled_errors: true,
  capture_unhandled_rejections: true,
  capture_console_errors: false,
};

const isBrowser = typeof window !== 'undefined';

let started = false;

// Which build the events came from — lets a single PostHog project separate
// desktop/mobile/web without three projects.
export function surface() {
  if (!isBrowser) return 'unknown';
  if (window.electronEnv) return 'desktop';
  if (window.Capacitor)   return 'mobile';
  return 'web';
}

// Desktop runs from file:// and mobile from capacitor://, and both use
// HashRouter — so the raw URL is useless for analysis ("file:///…/index.html").
// Normalise every surface onto the canonical web origin so one URL report
// covers all three.
const CANONICAL_ORIGIN = 'https://app.learnxops.com';
function canonicalUrl(pathname, search = '') {
  const path = pathname || '/';
  return `${CANONICAL_ORIGIN}${path}${search || ''}`;
}

// Collapse ids out of a path so PostHog can group by route instead of
// exploding into one row per lab/challenge/post:
//   /labs/kubernetes/7cd904ba-…  →  /labs/kubernetes/:id
const UUID_RE     = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;
export function routePattern(pathname) {
  return (pathname || '/')
    .split('/')
    .map((seg) => {
      if (!seg) return seg;
      if (UUID_RE.test(seg) || OBJECT_ID_RE.test(seg)) return ':id';
      if (/^\d+$/.test(seg)) return ':id';
      return seg;
    })
    .join('/') || '/';
}

export const isEnabled = () => started;

// Called once from index.js before React mounts.
export function initAnalytics() {
  if (started || !isBrowser || !TOKEN) return false;
  try {
    posthog.init(TOKEN, {
      api_host: HOST,
      ui_host: UI_HOST,
      // Pageviews are sent by hand from AnalyticsTracker: PostHog's automatic
      // capture fires once on load and never again under HashRouter, and would
      // record a file:// URL on desktop.
      capture_pageview: false,
      capture_pageleave: true,
      // Only create person profiles for signed-in users — anonymous visitors
      // still produce events, without the per-person cost.
      person_profiles: 'identified_only',
      // Off at init in every case. Recording is started below only for the
      // sampled sessions, which is what keeps the recorder script from being
      // fetched at all for the rest.
      disable_session_recording: true,
      session_recording: SESSION_RECORDING_OPTIONS,
      enable_recording_console_log: CONSOLE_LOGS_ENABLED,
      capture_exceptions: ERRORS_ENABLED ? EXCEPTION_OPTIONS : false,
      // Cookies are unavailable on file:// (desktop) and unreliable on
      // capacitor://; localStorage works on all three surfaces.
      persistence: 'localStorage+cookie',
      autocapture: true,
      // The project has surveys switched off, yet posthog-js was still
      // pulling surveys.js (33KB) on every page load. Saying so explicitly
      // stops the request.
      disable_surveys: true,
      loaded: (ph) => {
        if (process.env.NODE_ENV === 'development') ph.debug(false);
      },
    });

    // Stamped on every event so dev traffic and each surface stay separable.
    posthog.register({
      app_surface: surface(),
      app_env:     process.env.NODE_ENV === 'production' ? 'production' : 'development',
      app_version: process.env.REACT_APP_VERSION || null,
    });

    started = true;
    if (sampledIntoRecording()) {
      try { posthog.startSessionRecording(); } catch { /* best-effort */ }
    }
    return true;
  } catch {
    // Analytics must never take the app down.
    return false;
  }
}

// ── Event helpers ─────────────────────────────────────────────────────────────

// Report a caught error. Use in catch blocks where the app recovers but the
// failure still matters (a swallowed error is invisible to error tracking).
export function captureError(error, properties = {}) {
  if (!started || !error) return;
  try { posthog.captureException(error, properties); } catch { /* best-effort */ }
}

// Link to the replay of the session an error happened in — handy in a support
// reply or a bug report.
export function sessionReplayUrl() {
  if (!started) return null;
  try { return posthog.get_session_replay_url({ withTimestamp: true }); }
  catch { return null; }
}

// Member-facing opt out, and the way support turns replay on for a session
// that sampling did not pick. Events are untouched either way.
export function setSessionRecording(enabled) {
  if (!started) return;
  try { enabled ? posthog.startSessionRecording() : posthog.stopSessionRecording(); }
  catch { /* best-effort */ }
}

export function capture(event, properties = {}) {
  if (!started) return;
  try { posthog.capture(event, properties); } catch { /* best-effort */ }
}

export function capturePageview(pathname, search = '') {
  if (!started) return;
  try {
    posthog.capture('$pageview', {
      $current_url: canonicalUrl(pathname, search),
      $pathname:    pathname,
      route:        routePattern(pathname),
    });
  } catch { /* best-effort */ }
}

// Decode the JWT's `sub` claim for a stable distinct_id that survives email
// changes. Falls back to email when the token can't be read.
function userIdFrom(token, user) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (payload?.sub) return String(payload.sub);
  } catch { /* fall through to email */ }
  return user?.email || null;
}

export function identifyUser(token, user) {
  if (!started || !user) return;
  const id = userIdFrom(token, user);
  if (!id) return;
  try {
    posthog.identify(id, {
      email: user.email,
      name:  user.name,
      tier:  user.tier || 'free',
    });
    // Tier drives most segmentation, so keep it on every subsequent event too.
    posthog.register({ tier: user.tier || 'free' });
  } catch { /* best-effort */ }
}

// Clears the identity and starts a fresh anonymous session — important on
// desktop, where one machine is often shared and localStorage persists.
export function resetUser() {
  if (!started) return;
  try {
    posthog.unregister('tier');
    posthog.reset();
  } catch { /* best-effort */ }
}

export default posthog;
